<template>
  <div :class="[
    'p-tag-wrapper',
    {
      'p-tag-wrapper-rtl': rtl,
      'p-tag-wrapper-disabled': disabled,
      'p-tag-wrapper-readonly': readonly
    }
  ]">
    <Tag :value="value" :severity="severity" :rounded="rounded" :icon="icon" :class="[
      {
        'p-invalid': invalid,
        'p-disabled': disabled,
        'p-readonly': readonly
      }
    ]" :aria-label="ariaLabel" :aria-describedby="ariaDescribedby" :aria-required="required" :aria-invalid="invalid"
      :aria-disabled="disabled" :tabindex="disabled ? -1 : 0" v-bind="$attrs" @click="handleClick" @focus="handleFocus"
      @blur="handleBlur" role="status" />
    <small v-if="invalid && errorMessage" class="p-error">{{ errorMessage }}</small>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount } from 'vue'
import Tag from 'primevue/tag'
import type { TagProps, TagEmits } from './types'
import { useForm } from '../../../../composables/useForm'

defineOptions({
  name: 'ONETag'
})

const props = withDefaults(defineProps<TagProps>(), {
  value: undefined,
  severity: undefined,
  rounded: false,
  icon: undefined,
  invalid: false,
  required: false,
  disabled: false,
  readonly: false,
  ariaLabel: undefined,
  ariaDescribedby: undefined,
  rtl: false,
  errorMessage: undefined,
  formContext: undefined
})

const emit = defineEmits<TagEmits>()

// Form integration
if (props.formContext) {
  const form = useForm({
    [props.formContext]: props.value
  })

  if (props.required) {
    form.addValidation(props.formContext, [
      form.rules.required('Tag value is required')
    ])
  }

  onBeforeUnmount(() => {
    form.resetForm()
  })
}

// Event handlers
const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.readonly) {
    emit('click', event)
  }
}

const handleFocus = (event: FocusEvent) => {
  if (!props.disabled) {
    emit('focus', event)
  }
}

const handleBlur = (event: FocusEvent) => {
  if (!props.disabled) {
    emit('blur', event)
  }
}
</script>

<style lang="scss">
.p-tag-wrapper {
  display: inline-flex;
  flex-direction: column;
  gap: 0.25rem;

  &.p-tag-wrapper-rtl {
    direction: rtl;
  }

  &.p-tag-wrapper-disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.p-tag-wrapper-readonly {
    pointer-events: none;
  }

  .p-tag {
    &.p-invalid {
      border-color: var(--red-500);
      background-color: var(--red-100);
      color: var(--red-700);
    }

    &.p-disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.p-readonly {
      pointer-events: none;
    }

    &:focus {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }
  }

  .p-error {
    color: var(--red-700);
  }
}
</style>
